import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useMemo } from "react";
import { useAuthContext } from "@charphq/ui/providers/auth-provider";
import type { DashboardLink } from "@charphq/ui/components/pages/DashboardPage";
import {
  DashboardHealthSummary,
  type HealthNavTarget,
} from "@charphq/ui/components/health/dashboard-health-summary";
import { Building2, Layers, Radio, Smartphone, Users, Wrench } from "lucide-react";

const DashboardPage = lazy(() =>
  import("@charphq/ui/components/pages/DashboardPage").then((module) => ({
    default: module.DashboardPage,
  })),
);

const ICON_CLASS = "h-5 w-5 text-muted-foreground";

const DASHBOARD_LINKS: DashboardLink[] = [
  {
    label: "Customers",
    description: "Browse customer accounts and contacts.",
    href: "/customers",
    icon: <Building2 className={ICON_CLASS} />,
  },
  {
    label: "Devices",
    description: "Inspect connected LoRaWAN and Xtel devices.",
    href: "/devices",
    icon: <Radio className={ICON_CLASS} />,
  },
  {
    label: "Projects",
    description: "Open active and archived projects.",
    href: "/projects",
    icon: <Layers className={ICON_CLASS} />,
  },
  {
    label: "Deployments",
    description: "Review device deployments in the field.",
    href: "/deployments",
    icon: <Smartphone className={ICON_CLASS} />,
  },
  {
    label: "Calibrations",
    description: "Track calibration records and runs.",
    href: "/calibrations",
    icon: <Wrench className={ICON_CLASS} />,
  },
  {
    label: "Users",
    description: "Manage users and team membership.",
    href: "/users",
    icon: <Users className={ICON_CLASS} />,
  },
];

export const Route = createFileRoute("/dashboard")({
  component: function Dashboard() {
    const { isAuthenticated, user, signOut, isLoading } = useAuthContext();
    const navigate = useNavigate();
    const userInfo = useMemo(
      () => (user ? { name: user.name, email: user.email } : undefined),
      // eslint-disable-next-line react-hooks/exhaustive-deps -- granular deps to avoid re-renders on unrelated user fields
      [user?.email, user?.name],
    );

    if (isLoading) {
      return (
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return null;
    }

    return (
      <Suspense
        fallback={
          <div className="flex justify-center items-center min-h-screen">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        }
      >
        <DashboardPage
          user={userInfo}
          onSignOut={signOut}
          links={DASHBOARD_LINKS}
          onNavigate={(href) => navigate({ to: href })}
          summarySlot={
            <DashboardHealthSummary
              onNavigateHealth={(target?: HealthNavTarget) =>
                navigate({ to: "/system-health", search: target ?? {} })
              }
            />
          }
        />
      </Suspense>
    );
  },
});
